import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView, useMotionValue, useTransform, useMotionTemplate } from 'motion/react';
import { Clock, ShieldAlert, CheckCircle2, Wrench, Siren, Zap, ArrowRight, ShieldCheck, Hourglass } from 'lucide-react';
import HeroInteractive from './HeroInteractive';

function useLightMode() {
  const [isLightMode, setIsLightMode] = useState(() => 
    typeof document !== 'undefined' && document.documentElement.classList.contains('light-mode')
  );

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const observer = new MutationObserver(() => {
      setIsLightMode(document.documentElement.classList.contains('light-mode'));
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, []);

  return isLightMode;
}

export default function ResponseTime() {
  const isLightMode = useLightMode();
  const containerRef = useRef<HTMLDivElement>(null);
  const inView = useInView(containerRef, { margin: "-20%" });
  
  // Físicas interactivas para la Perspectiva 3D
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useTransform(y, [-500, 500], [10, -10]);
  const rotateY = useTransform(x, [-500, 500], [-10, 10]);
  
  // Variables para la linterna/reflector del mouse
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  function handleMouseMove(event: React.MouseEvent<HTMLDivElement>) {
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      x.set(event.clientX - centerX);
      y.set(event.clientY - centerY);
      mouseX.set(event.clientX - rect.left);
      mouseY.set(event.clientY - rect.top);
    }
  }

  function handleMouseLeave() {
    x.set(0);
    y.set(0);
  }

  // Detectar idioma
  const isEn = typeof document !== 'undefined' && document.documentElement.lang === 'en';

  return (
    <section className={`py-24 ${isLightMode ? 'bg-[#f8fafc] border-slate-200' : 'bg-[#030a21] border-white/5'} relative overflow-hidden border-t border-b`}>
      {/* Background: Luces Ambientales Base */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full max-w-[1200px] pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#FF6B00]/5 blur-[180px] rounded-full mix-blend-screen opacity-40" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#007AFF]/10 blur-[150px] rounded-full mix-blend-screen opacity-40" />
      </div>

      {/* Background: Sistema de Anillos de Radar */}
      <div className="absolute inset-0 z-0 flex items-center justify-center opacity-15 pointer-events-none">
        {[1, 2, 3, 4].map((ring) => (
          <motion.div
            key={ring}
            animate={{ 
              scale: [1, 1.15, 1],
              opacity: [0.1, 0.3, 0.1]
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              delay: ring * 1.0,
              ease: "easeInOut"
            }}
            className="absolute rounded-full border border-[#007AFF]/25"
            style={{
              width: `${ring * 320}px`,
              height: `${ring * 320}px`,
            }}
          />
        ))}
        {/* Haz de barrido tipo radar */}
        <motion.div 
          animate={{ rotate: 360 }}
          transition={{ duration: 7, repeat: Infinity, ease: "linear" }}
          className="absolute w-[600px] h-[600px] rounded-full"
          style={{
            background: "conic-gradient(from 0deg, transparent 75%, rgba(0, 122, 255, 0.15) 95%, rgba(0, 122, 255, 0.6) 100%)",
            maskImage: "radial-gradient(black, transparent)",
            WebkitMaskImage: "radial-gradient(black, transparent)",
          }}
        />
      </div>

      <div className="max-w-[1440px] w-full mx-auto px-6 md:px-12 lg:px-20 relative z-10 flex flex-col items-center">
        
        {/* Cabecera Descriptiva */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16 max-w-3xl mx-auto"
        >
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border ${isLightMode ? 'border-[#FF6B00]/45 bg-[#FF6B00]/8 text-[#c2410c] shadow-[0_0_20px_rgba(255,107,0,0.08)]' : 'border-[#FF6B00]/30 bg-[#FF6B00]/10 text-white shadow-[0_0_20px_rgba(255,107,0,0.15)]'} font-semibold text-xs md:text-sm mb-6 uppercase tracking-widest backdrop-blur-md`}>
            <Siren className="w-4 h-4 text-[#FF6B00] animate-pulse" />
            <span>{isEn ? "Emergency Response Capability" : "Capacidad de Respuesta Ante Contingencias"}</span>
          </div>
          <h2 className={`text-4xl md:text-5xl lg:text-6xl font-black ${isLightMode ? 'text-[#0f172a]' : 'text-white'} mb-6 tracking-tight leading-tight`}>
            {isEn ? (
              <>Critical Contingency in your <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#007AFF] to-[#00F0FF]">Plant, Pipelines or Infrastructure?</span></>
            ) : (
              <>¿Contingencia Crítica en su <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#007AFF] to-[#00F0FF]">Planta, Ductos o Infraestructura?</span></>
            )}
          </h2>
          <div className="text-xl md:text-2xl font-normal leading-relaxed drop-shadow-sm">
            <motion.span 
              animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
              transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
              className="block text-2xl md:text-4xl font-black mb-4 text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B00] via-[#FFB800] to-[#FF6B00] bg-[length:200%_auto] uppercase tracking-tight"
            >
              {isEn ? "We are the undisputed leaders of the industry!" : "¡Somos los líderes indiscutibles de la industria!"}
            </motion.span>
            <span className={`block ${isLightMode ? 'text-slate-600' : 'text-gray-300'}`}>
              {isEn ? (
                <>Specialists in the comprehensive resolution of contingencies in oil plants, piping and pipelines. We restore your operations in <strong>Record Time</strong>, minimizing losses and ensuring safe continuity.</>
              ) : (
                <>Especialistas en la resolución integral de contingencias en plantas, tuberías y ductos petroleros. Restauramos su operatividad en <strong>Tiempo Récord</strong> minimizando pérdidas y garantizando continuidad segura.</>
              )}
            </span>
          </div>
        </motion.div>

        {/* ELEMENTO INTERACTIVO: RADAR HUD CON ALTERNANCIA DE CTAS */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="w-full mb-16 flex justify-center"
        >
          <HeroInteractive />
        </motion.div>

        {/* Tarjeta Núcleo 3D Interactiva Comparativa "VS" */}
        <motion.div
          ref={containerRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative w-full max-w-5xl mx-auto perspective-[2000px] cursor-crosshair group"
        >
          {/* Panel Base Principal */}
          <div className={`relative z-10 ${isLightMode ? 'bg-[#ffffff] border-slate-200 shadow-[0_30px_100px_-20px_rgba(0,122,255,0.06)]' : 'bg-[#00133c]/60 border-white/10 shadow-[0_30px_100px_-20px_rgba(0,122,255,0.2)]'} backdrop-blur-3xl border rounded-[3rem] p-6 md:p-12 overflow-hidden transition-colors duration-500 group-hover:border-[#007AFF]/30`}>
            
            {/* Linterna/Foco de seguimiento del Mouse */}
            <motion.div
              className="absolute inset-0 opacity-0 group-hover:opacity-100 mix-blend-screen transition-opacity duration-500 pointer-events-none z-0"
              style={{
                background: useMotionTemplate`radial-gradient(700px circle at ${mouseX}px ${mouseY}px, ${isLightMode ? 'rgba(0,122,255,0.05)' : 'rgba(0,122,255,0.12)'}, transparent 45%)`,
              }}
            />

            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5 pointer-events-none"></div>

            <div className="relative z-10 grid md:grid-cols-2 gap-8 md:gap-12 items-stretch">
              
              {/* LADO IZQUIERDO: Competencia Tradicional (Peligro/Demora) */}
              <div 
                className={`flex flex-col justify-between p-6 md:p-8 rounded-2xl ${isLightMode ? 'bg-red-500/5 border-red-200' : 'bg-red-950/10 border-red-500/20'} border relative`} 
                style={{ transform: "translateZ(50px)" }}
              >
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <span className={`font-bold text-xs uppercase tracking-widest ${isLightMode ? 'bg-red-100 text-red-700 border-red-200' : 'bg-red-950/40 text-red-400 border-red-500/25'} px-3 py-1 rounded-full border`}>
                      {isEn ? "Traditional Competence" : "Competencia Tradicional"}
                    </span>
                    <Hourglass className={`w-6 h-6 ${isLightMode ? 'text-red-500' : 'text-red-400'} animate-pulse`} />
                  </div>
                  
                  <div className="flex items-baseline gap-2 mb-6">
                    <span className="text-7xl md:text-8xl font-black text-red-500 font-sans tracking-tight leading-none drop-shadow-[0_0_20px_rgba(239,68,68,0.3)]">
                      15+
                    </span>
                    <div>
                      <span className={`block text-xl md:text-2xl font-black ${isLightMode ? 'text-slate-800' : 'text-gray-300'} uppercase tracking-tighter`}>
                        {isEn ? "Days" : "Días"}
                      </span>
                      <span className={`block ${isLightMode ? 'text-red-600' : 'text-red-400'} font-bold text-[10px] tracking-widest uppercase`}>
                        {isEn ? "Typical Wait" : "Espera Típica"}
                      </span>
                    </div>
                  </div>

                  <h4 className={`text-xl font-bold ${isLightMode ? 'text-[#0f172a]' : 'text-white'} mb-6`}>
                    {isEn ? "Slow and Bureaucratic Response" : "Respuesta Lenta y Burocrática"}
                  </h4>
                  
                  <ul className={`flex flex-col gap-4 ${isLightMode ? 'text-slate-600' : 'text-gray-300'} text-sm md:text-base`}>
                    <li className="flex items-start gap-3">
                      <ShieldAlert className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                      <span>{isEn ? "Prolonged logistical demobilization and delayed response." : "Desmovilización logística prolongada y respuesta tardía."}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <ShieldAlert className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                      <span>{isEn ? "Excessive paperwork and complex administrative processes." : "Excesivo papeleo y trámites administrativos complejos."}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <ShieldAlert className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                      <span>{isEn ? "Million-dollar losses due to complete production shutdown." : "Pérdidas millonarias debido a la parada total de producción."}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <ShieldAlert className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                      <span>{isEn ? "Low technological level in intervention machinery." : "Bajo nivel tecnológico en maquinaria de intervención."}</span>
                    </li>
                  </ul>
                </div>
                
                <div className={`mt-8 pt-6 border-t ${isLightMode ? 'border-red-100' : 'border-red-500/10'} text-center md:text-left`}>
                  <span className={`text-xs ${isLightMode ? 'text-red-600' : 'text-red-400/70'} font-semibold tracking-wider`}>
                    {isEn ? "CONVENTIONAL MARKET" : "MERCADO CONVENCIONAL"}
                  </span>
                </div>
              </div>

              {/* LADO DERECHO: El Estándar Prointeca (Éxito/Inmediato) */}
              <div 
                className={`flex flex-col justify-between p-6 md:p-8 rounded-2xl ${isLightMode ? 'bg-[#007AFF]/5 border-[#007AFF]/25 shadow-[0_0_30px_rgba(0,122,255,0.04)]' : 'bg-blue-950/20 border border-[#007AFF]/30 shadow-[0_0_30px_rgba(0,122,255,0.15)]'} relative`} 
                style={{ transform: "translateZ(80px)" }}
              >
                {/* Spotlight interno de la tarjeta de éxito */}
                <div className={`absolute -inset-10 ${isLightMode ? 'bg-[#007AFF]/5' : 'bg-[#007AFF]/10'} blur-[50px] rounded-full pointer-events-none -z-10`}></div>
                <div className="absolute -inset-10 bg-[#FF6B00]/5 blur-[60px] rounded-full pointer-events-none -z-10"></div>

                <div>
                  <div className="flex items-center justify-between mb-6">
                    <span className={`font-bold text-xs uppercase tracking-widest ${isLightMode ? 'bg-[#007AFF]/10 text-[#0066cc] border-[#007AFF]/20' : 'bg-blue-950/60 text-cyan-400 border-cyan-500/30'} px-3 py-1 rounded-full border`}>
                      {isEn ? "Standard" : "Estándar"} <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B00] via-[#FFB800] to-[#FFD700] uppercase font-black">PROINTECA</span>
                    </span>
                    <Zap className="w-6 h-6 text-[#FF6B00] animate-bounce" />
                  </div>
                  
                  <div className="flex items-baseline gap-2 mb-6">
                    <span className="text-7xl md:text-8xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-500 via-[#007AFF] to-[#FF6B00] font-sans tracking-tight leading-none drop-shadow-[0_0_30px_rgba(0,122,255,0.5)]">
                      72
                    </span>
                    <div>
                      <span className={`block text-xl md:text-2xl font-black ${isLightMode ? 'text-slate-800' : 'text-white'} uppercase tracking-tighter`}>
                        {isEn ? "Hr" : "Hr"}
                      </span>
                      <span className="block text-[#FF6B00] font-black text-xs tracking-widest uppercase">
                        {isEn ? "Maximum" : "Máximo"}
                      </span>
                    </div>
                  </div>

                  <h4 className={`text-xl font-bold ${isLightMode ? 'text-[#0f172a]' : 'text-white'} mb-6`}>
                    {isEn ? "Tactical Action and Rehabilitation" : "Rehabilitación y Acción Táctica"}
                  </h4>
                  
                  <ul className={`flex flex-col gap-4 ${isLightMode ? 'text-slate-600' : 'text-gray-300'} text-sm md:text-base`}>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                      <span>{isEn ? "Immediate 24/7 operational deployment of tactical field squadron." : "Despliegue operativo inmediato 24/7 de escuadrón táctico en campo."}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                      <span>{isEn ? "Coordinated and agile Engineering, Procurement and Construction (EPC)." : "Ingeniería, Procura y Construcción (IPC) coordinada y ágil."}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                      <span>{isEn ? "Safe hot tapping (Hot Tapping) without plant shutdown." : "Perforaciones seguras en caliente (Hot Tapping) sin paralizar la planta."}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                      <span>{isEn ? "Minimal incidents, absolute environmental protection, and guaranteed integrity." : "Mínimos incidentes, resguardo ambiental absoluto e integridad garantizada."}</span>
                    </li>
                  </ul>
                </div>

                <div className={`mt-8 pt-6 border-t ${isLightMode ? 'border-[#007AFF]/15' : 'border-[#007AFF]/20'} flex items-center justify-between`}>
                  <span className={`text-xs ${isLightMode ? 'text-[#0056b3]' : 'text-cyan-400'} font-semibold tracking-wider`}>
                    {isEn ? "PROVEN EFFICACY" : "EFICACIA COMPROBADA"}
                  </span>
                  <div className="flex items-center gap-1">
                    <ShieldCheck className="w-4 h-4 text-emerald-500" />
                    <span className={`text-xs ${isLightMode ? 'text-[#0f172a]' : 'text-white'} font-bold`}>
                      {isEn ? "100% Flawless" : "100% Impecable"}
                    </span>
                  </div>
                </div>
              </div>

            </div>
            
            {/* Detalles estéticos del marco HUD */}
            <div className={`absolute inset-0 pointer-events-none border-[1px] border-dashed ${isLightMode ? 'border-[#007AFF]/10' : 'border-[#007AFF]/15'} rounded-[3rem] m-2`}></div>
            <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-px h-full ${isLightMode ? 'bg-gradient-to-b from-transparent via-[#007AFF]/5 to-transparent' : 'bg-gradient-to-b from-transparent via-[#007AFF]/10 to-transparent'} hidden md:block`}></div>
          </div>
          
          {/* Insignias flotantes satelitales */}
          <div 
            className="absolute -top-6 -right-4 bg-gradient-to-r from-[#FF6B00] to-[#FF8A00] text-white font-black text-[10px] md:text-xs px-4 py-2 rounded-full shadow-[0_0_30px_#FF6B00] flex items-center gap-2 transform rotate-6"
            style={{ transform: "translateZ(120px) rotate(6deg)" }}
          >
            <ShieldCheck className="w-4 h-4" />
            {isEn ? "100% GUARANTEED" : "100% GARANTIZADO"}
          </div>
          
          <div 
            className={`absolute -bottom-8 left-6 md:left-12 ${isLightMode ? 'bg-white border-slate-200 shadow-[0_10px_35px_rgba(0,122,255,0.06)] text-slate-800' : 'bg-[#0A60D0]/95 border-[rgba(255,255,255,0.1)] shadow-[0_0_30px_rgba(0,122,255,0.4)] text-white'} font-bold text-xs px-4 py-3 rounded-2xl flex items-center gap-3 backdrop-blur-md border`}
            style={{ transform: "translateZ(100px)" }}
          >
            <Clock className={`w-5 h-5 ${isLightMode ? 'text-[#007AFF]' : 'text-cyan-300'} animate-pulse`} />
            <div>
              <div className={`text-[9px] ${isLightMode ? 'text-slate-500' : 'text-white opacity-90'} uppercase tracking-widest leading-none mb-1`}>
                {isEn ? "Your plant operating in" : "Tu planta operando en"}
              </div>
              <div className={`leading-none ${isLightMode ? 'text-[#0f172a]' : 'text-white'} font-black`}>
                {isEn ? "Record Time" : "Tiempo Récord"}
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA (Llamado a la acción) */}
        <motion.div
           initial={{ opacity: 0 }}
           whileInView={{ opacity: 1 }}
           transition={{ delay: 0.6 }}
           className="mt-16 text-center"
        >
          <a href={isEn ? "/en/contact" : "/contacto"} className={`inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full ${isLightMode ? 'bg-[#007AFF] text-white hover:bg-[#FF6B00] hover:text-white' : 'bg-white text-[#050B14] hover:bg-[#FF6B00] hover:text-white'} font-bold hover:shadow-[0_0_35px_rgba(255,107,0,0.45)] transition-all duration-300 transform hover:-translate-y-1`}>
            {isEn ? "I Require Immediate Assistance" : "Requiero Asistencia Inmediata"}
            <ArrowRight className="w-5 h-5" />
          </a>
        </motion.div>

      </div>
    </section>
  );
}
